import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOpenAIStatus, useOpenAIDeviceCode, useOpenAIDevicePoll, useOpenAIDisconnect } from '@/api/hooks/useOpenAI'
import {
  useAiSettings, useSetAiProvider, useSaveGeminiKey, useRemoveGeminiKey, useGeminiModels,
  type AiProvider, type GeminiModel,
} from '@/api/hooks/useAiSettings'
import { useLanguage } from '@/i18n/LanguageContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { Settings as SettingsIcon, Zap, ExternalLink, Loader2, Copy, Check, Unlink, Sparkles, KeyRound, Trash2, CheckCircle2 } from 'lucide-react'

const GEMINI_DEFAULT_MODEL = 'gemini-2.5-flash'
// Lista inicial (antes de carregar da API com a chave do usuario)
const GEMINI_FALLBACK_MODELS: GeminiModel[] = [
  { name: 'gemini-2.5-flash', displayName: 'Gemini 2.5 Flash' },
  { name: 'gemini-2.5-flash-lite', displayName: 'Gemini 2.5 Flash Lite' },
  { name: 'gemini-flash-latest', displayName: 'Gemini Flash (latest)' },
]
const CUSTOM_MODEL = '__custom__'

export function Settings() {
  const { t } = useLanguage()
  const queryClient = useQueryClient()
  const { data: status, isLoading: statusLoading } = useOpenAIStatus()
  const deviceCode = useOpenAIDeviceCode()
  const disconnect = useOpenAIDisconnect()

  const [deviceData, setDeviceData] = useState<{
    device_auth_id: string
    user_code: string
    verification_uri: string
  } | null>(null)
  const [polling, setPolling] = useState(false)
  const [copied, setCopied] = useState(false)

  const poll = useOpenAIDevicePoll(
    deviceData?.device_auth_id ?? null,
    deviceData?.user_code ?? null,
    polling,
  )

  useEffect(() => {
    if (poll.data?.status === 'authorized') {
      setPolling(false)
      setDeviceData(null)
      queryClient.invalidateQueries({ queryKey: ['openai', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['ai', 'settings'] })
      toast.success('ChatGPT conectado com sucesso!')
    }
    if (poll.error) {
      setPolling(false)
      setDeviceData(null)
      toast.error('Erro na conexao. Tente novamente.')
    }
  }, [poll.data?.status, poll.error])

  const handleConnect = async () => {
    try {
      const data = await deviceCode.mutateAsync()
      setDeviceData(data)
      setPolling(true)
    } catch {
      toast.error('Erro ao iniciar conexao com OpenAI')
    }
  }

  const handleDisconnect = () => {
    if (!confirm('Desconectar sua conta ChatGPT?')) return
    disconnect.mutate(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['ai', 'settings'] })
        toast.success('ChatGPT desconectado')
      },
      onError: () => toast.error('Erro ao desconectar'),
    })
  }

  const handleCopy = () => {
    if (deviceData?.user_code) {
      navigator.clipboard.writeText(deviceData.user_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // ── Provedor de IA (por usuario) + Gemini ──
  const { data: aiSettings, isLoading: aiLoading } = useAiSettings()
  const setProvider = useSetAiProvider()
  const saveGemini = useSaveGeminiKey()
  const removeGemini = useRemoveGeminiKey()
  const loadModels = useGeminiModels()

  const provider: AiProvider = aiSettings?.provider ?? 'chatgpt'
  const gemini = aiSettings?.gemini
  const chatgptConnected = status?.chatgptConnected ?? status?.connected ?? false

  // Aba aberta (so VISUALIZA; trocar a IA ativa e no botao "Usar este").
  // Abre na IA ativa quando as configuracoes carregam pela primeira vez.
  const [tab, setTab] = useState<AiProvider>('chatgpt')
  const tabInit = useRef(false)
  useEffect(() => {
    if (!tabInit.current && aiSettings) { setTab(aiSettings.provider); tabInit.current = true }
  }, [aiSettings])

  const [editingGemini, setEditingGemini] = useState(false)
  const [geminiKey, setGeminiKey] = useState('')
  const [modelChoice, setModelChoice] = useState(GEMINI_DEFAULT_MODEL)
  const [customModel, setCustomModel] = useState('')
  const [models, setModels] = useState<GeminiModel[]>(GEMINI_FALLBACK_MODELS)

  const showGeminiForm = !gemini?.configured || editingGemini
  const selectedModel = modelChoice === CUSTOM_MODEL ? customModel.trim() : modelChoice

  const openModelEditor = () => {
    const current = gemini?.model || GEMINI_DEFAULT_MODEL
    if (models.some(m => m.name === current)) setModelChoice(current)
    else { setModelChoice(CUSTOM_MODEL); setCustomModel(current) }
    setGeminiKey('')
    setEditingGemini(true)
  }

  const handleLoadModels = () => {
    const key = geminiKey.trim()
    if (!key && !gemini?.configured) { toast.error('Cole a chave primeiro'); return }
    loadModels.mutate({ key: key || undefined }, {
      onSuccess: ({ models: list }) => {
        if (list.length === 0) { toast.error('Nenhum modelo de texto disponivel para essa chave'); return }
        setModels(list)
        if (!list.some(m => m.name === modelChoice) && modelChoice !== CUSTOM_MODEL) setModelChoice(list[0].name)
        toast.success(`${list.length} modelos carregados`)
      },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleSaveGemini = () => {
    const key = geminiKey.trim()
    if (!key && !gemini?.configured) { toast.error('Cole a chave do Gemini'); return }
    if (!selectedModel) { toast.error('Informe o modelo'); return }
    saveGemini.mutate({ key: key || undefined, model: selectedModel }, {
      onSuccess: () => {
        setGeminiKey('')
        setEditingGemini(false)
        toast.success(key ? 'Chave do Gemini valida e salva' : 'Modelo atualizado')
      },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleRemoveGemini = () => {
    if (!confirm('Remover a chave do Gemini? Se o Gemini estiver escolhido, volta para o ChatGPT.')) return
    removeGemini.mutate(undefined, {
      onSuccess: () => { setEditingGemini(false); toast.success('Chave do Gemini removida') },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleSetProvider = (p: AiProvider) => {
    if (p === provider) return
    setProvider.mutate(p, {
      onSuccess: () => toast.success(p === 'gemini' ? 'Usando o Gemini' : 'Usando o ChatGPT'),
      onError: (err) => toast.error(err.message),
    })
  }

  // Controle "Em uso / Usar este" reaproveitado nos dois paineis
  const ProviderUseControl = ({ p, ready }: { p: AiProvider; ready: boolean }) => (
    provider === p ? (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-500">
        <CheckCircle2 className="h-3.5 w-3.5" /> {t('Em uso')}
      </span>
    ) : (
      <Button size="sm" variant="outline" onClick={() => handleSetProvider(p)}
        disabled={!ready || setProvider.isPending}
        title={!ready ? (p === 'gemini' ? 'Configure a chave do Gemini primeiro' : 'Conecte o ChatGPT primeiro') : undefined}
        className="gap-1.5">
        {setProvider.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {t('Usar este')}
      </Button>
    )
  )

  // Aba (so visualiza). Bolinha = pronto pra uso.
  const tabButton = (p: AiProvider, label: string, Icon: typeof Zap, ready: boolean) => (
    <button type="button" onClick={() => setTab(p)}
      className={cn(
        'relative flex items-center gap-2 rounded-t-lg border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
        tab === p
          ? 'border-primary text-foreground bg-card'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40',
      )}>
      <Icon className="h-4 w-4" />
      {label}
      <span className={cn('h-1.5 w-1.5 rounded-full', ready ? 'bg-emerald-500' : 'bg-muted-foreground/40')}
        title={ready ? 'Pronto' : 'Falta configurar'} />
      {provider === p && (
        <span className="ml-0.5 rounded bg-emerald-500/10 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-500">
          {t('ativo')}
        </span>
      )}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <SettingsIcon className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold tracking-tight">{t('Configuracoes')}</h1>
      </div>

      {/* Card unico: Inteligencia Artificial */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {/* Cabecalho do card */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">{t('Inteligencia Artificial')}</h2>
              <p className="text-xs text-muted-foreground">
                {t('Usada em legendas, YouTube, chat, agente e analise de clipe')}
              </p>
            </div>
          </div>
          {aiLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Badge className={cn(
              'gap-1.5 border',
              provider === 'gemini'
                ? 'bg-sky-500/10 text-sky-500 border-sky-500/30 hover:bg-sky-500/10'
                : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10',
            )}>
              <CheckCircle2 className="h-3 w-3" />
              {t('Em uso')}: {provider === 'gemini' ? 'Gemini' : 'ChatGPT'}
            </Badge>
          )}
        </div>

        {/* Abas */}
        <div className="flex items-end gap-1 border-b border-border bg-muted/20 px-3 pt-2">
          {tabButton('chatgpt', 'ChatGPT', Zap, chatgptConnected)}
          {tabButton('gemini', 'Gemini', KeyRound, !!gemini?.configured)}
        </div>

        {/* Painel da aba aberta */}
        <div className="px-5 py-5">
          {/* ───── ChatGPT ───── */}
          {tab === 'chatgpt' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">OpenAI / ChatGPT</span>
                  {statusLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : chatgptConnected ? (
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10">{t('Conectado')}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">{t('Desconectado')}</Badge>
                  )}
                </div>
                <ProviderUseControl p="chatgpt" ready={chatgptConnected} />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('Conecte sua conta ChatGPT para usar IA no Blissf')}
              </p>

              {/* Conectado */}
              {chatgptConnected && !deviceData && (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs text-muted-foreground">
                    {t('Conta ChatGPT conectada desde')} {status?.connectedAt ? new Date(status.connectedAt).toLocaleDateString('pt-BR') : '—'}
                  </p>
                  <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnect.isPending}
                    className="gap-1.5 text-destructive hover:text-destructive">
                    <Unlink className="h-3.5 w-3.5" /> {t('Desconectar')}
                  </Button>
                </div>
              )}

              {/* Fluxo do codigo */}
              {deviceData && polling && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                    <p className="text-sm font-medium">{t('Insira o codigo abaixo no site da OpenAI:')}</p>
                    <div className="flex items-center gap-2">
                      <code className="rounded-md bg-background border border-border px-4 py-2 text-2xl font-bold tracking-[0.3em] select-all">
                        {deviceData.user_code}
                      </code>
                      <Button variant="ghost" size="icon" onClick={handleCopy} className="shrink-0">
                        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <Button size="sm" className="gap-1.5" onClick={() => window.open(deviceData.verification_uri, '_blank')}>
                      <ExternalLink className="h-3.5 w-3.5" /> {t('Abrir OpenAI')}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('Aguardando autorizacao...')}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setPolling(false); setDeviceData(null) }} className="text-xs">
                    {t('Cancelar')}
                  </Button>
                </div>
              )}

              {/* Nao conectado */}
              {!chatgptConnected && !deviceData && !statusLoading && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {t('Conecte sua conta ChatGPT para usar modelos da OpenAI. Voce usara os creditos da sua assinatura do ChatGPT.')}
                  </p>
                  <Button size="sm" onClick={handleConnect} disabled={deviceCode.isPending} className="gap-1.5">
                    {deviceCode.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                    {t('Conectar com ChatGPT')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ───── Gemini ───── */}
          {tab === 'gemini' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Google Gemini</span>
                  {aiLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : gemini?.configured ? (
                    <Badge className="bg-sky-500/10 text-sky-600 border-sky-500/30 hover:bg-sky-500/10">{t('Configurada')}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">{t('Nao configurada')}</Badge>
                  )}
                </div>
                <ProviderUseControl p="gemini" ready={!!gemini?.configured} />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('Use sua chave do Google AI Studio (tier gratuito do Gemini Flash)')}
              </p>

              {gemini?.configured && !editingGemini && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {t('Configurada desde')} {gemini.connectedAt ? new Date(gemini.connectedAt).toLocaleDateString('pt-BR') : '—'}
                    {' · '}{t('chave')} …{gemini.keyLast4}
                    {' · '}{t('modelo')} <code className="text-[11px]">{gemini.model}</code>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={openModelEditor} className="gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> {t('Trocar modelo')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setGeminiKey(''); setEditingGemini(true) }} className="gap-1.5">
                      <KeyRound className="h-3.5 w-3.5" /> {t('Trocar chave')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleRemoveGemini} disabled={removeGemini.isPending}
                      className="gap-1.5 text-destructive hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" /> {t('Remover chave')}
                    </Button>
                  </div>
                </>
              )}

              {showGeminiForm && (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">{t('Chave de API')}{gemini?.configured ? ` (${t('deixe em branco para manter a atual')})` : ''}</label>
                    <div className="flex gap-2">
                      <Input type="password" value={geminiKey} onChange={e => setGeminiKey(e.target.value)}
                        placeholder="AIza..." autoComplete="off" className="h-9 text-xs font-mono" />
                      <Button variant="outline" size="sm" onClick={handleLoadModels} disabled={loadModels.isPending} className="shrink-0 gap-1.5">
                        {loadModels.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        {t('Carregar modelos')}
                      </Button>
                    </div>
                    <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
                      <ExternalLink className="h-3 w-3" /> {t('Obter chave no Google AI Studio')}
                    </a>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">{t('Modelo')}</label>
                    <div className="flex flex-wrap gap-2">
                      <Select value={modelChoice} onValueChange={setModelChoice}>
                        <SelectTrigger className="h-9 w-72 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {models.map(m => (<SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>))}
                          <SelectItem value={CUSTOM_MODEL}>{t('Outro (digitar)')}</SelectItem>
                        </SelectContent>
                      </Select>
                      {modelChoice === CUSTOM_MODEL && (
                        <Input value={customModel} onChange={e => setCustomModel(e.target.value)}
                          placeholder="gemini-..." className="h-9 w-60 text-xs font-mono" />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={handleSaveGemini} disabled={saveGemini.isPending} className="gap-1.5">
                      {saveGemini.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      {t('Salvar e testar')}
                    </Button>
                    {gemini?.configured && (
                      <Button variant="ghost" size="sm" onClick={() => setEditingGemini(false)} className="text-xs">
                        {t('Cancelar')}
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {t('A chave e testada com uma chamada minima antes de ser salva. Se o projeto estiver sem saldo ou no limite, a chave e salva com aviso.')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
